import type { BookMetadata, OutputChapter, OutputVerse } from '../../types';
import _booksMetadata from '../text/books.json';
import { AudioPlayer } from './audio-player';
import { BibleApp } from './bible-app';

declare global {
  interface Window {
    app: App;
  }
}

interface SearchResult {
  book: BookMetadata;
  chapter: OutputChapter;
  verse: OutputVerse;
  relevance: number;
}

const booksMetadata = _booksMetadata as Readonly<{ books: BookMetadata[] }>;

const textFiles = import.meta.glob('/text/*.json') as {
  [url: string]: () => Promise<OutputChapter>;
};
const pdfFiles = import.meta.glob('/pdf/*.pdf', { eager: true }) as {
  [url: string]: { default: string };
};
console.assert(Object.keys(pdfFiles).length > 0);
/*
 * pdfFiles = {
 *   '/pdf/AEB2_website_MAT-001_ptxp.pdf': {
 *     'default': '/pdf/AEB2_website_MAT-001_ptxp-U8eug88.pdf',
 *   },
 *   ...
 * }
 */

class App {
  audioPlayer: AudioPlayer;
  bibleApp: BibleApp;

  constructor() {
    this.audioPlayer = new AudioPlayer();
    this.bibleApp = new BibleApp(this.audioPlayer);
    this.init();
  }

  async init() {
    try {
      // Load Bible data
      await this.bibleApp.loadData();

      // Setup event listeners
      this.setupEventListeners();

      // Initialize theme from localStorage
      this.initializeTheme();

      // Initialize font size from localStorage
      this.initializeFontSize();

      this.resolveNavigation(document.location.toString()).catch((e) =>
        console.error(e),
      );

      console.log('App initialized successfully');
    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.showError('فشل في تحميل البيانات. يرجى إعادة تحميل الصفحة.');
    }
  }

  setupEventListeners() {
    if ('navigation' in window) {
      window.navigation.addEventListener('navigate', (event) => {
        if (
          !event.canIntercept ||
          event.hashChange ||
          event.downloadRequest ||
          event.formData
        ) {
          return;
        }

        const that = this;
        event.intercept({
          async handler() {
            await that.resolveNavigation(event.destination.url);
          },
        });
      });

      window.navigation.addEventListener('navigateerror', (event) => {
        console.error(event.error);
      });
    } else {
      console.warn('Could not find window.navigation');
    }

    // Search functionality
    document.getElementById('search-btn')!.addEventListener('click', () => {
      this.toggleSearchPanel();
    });

    document.getElementById('search-input')!.addEventListener('input', (e) => {
      if (e.target) {
        const target = e.target as HTMLInputElement;
        this.handleSearch(target.value).catch((e) => console.error(e));
      }
    });

    // Theme toggle
    document.getElementById('theme-btn')!.addEventListener('click', () => {
      this.toggleTheme();
    });

    document.getElementById('pdf-btn')!.addEventListener('click', () => {
      this.openPDF();
    });

    // Font size control
    document.getElementById('font-size-btn')!.addEventListener('click', () => {
      this.toggleFontPanel();
    });

    document
      .getElementById('font-decrease-btn')!
      .addEventListener('click', () => {
        this.decreaseFontSize();
      });

    document
      .getElementById('font-increase-btn')!
      .addEventListener('click', () => {
        this.increaseFontSize();
      });

    // Navigation
    document.getElementById('back-btn')!.addEventListener('click', () => {
      this.bibleApp.goUp();
    });

    // Audio player controls are handled by the AudioPlayer class

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      this.handleKeyboardShortcuts(e);
    });

    // Close panels when clicking outside
    document.addEventListener('click', (e) => {
      this.handleOutsideClick(e);
    });
  }

  async resolveNavigation(urlString: string) {
    const url = new URL(urlString);
    const path = url.pathname.split('/').filter((x) => x);
    if (path.length === 0) {
      this.bibleApp.renderBooksView();
    } else if (path.length === 1) {
      const book = booksMetadata.books.find((b) => b.id === path[0]);
      if (book) {
        this.bibleApp.showBook(book);
      }
    } else if (path.length === 2) {
      const book = booksMetadata.books.find((b) => b.id === path[0]);
      const chapterNumber = parseInt(path[1], 10);
      if (book) {
        await this.bibleApp.showChapter(book, chapterNumber);
      }
    }
  }

  toggleSearchPanel() {
    const panel = document.getElementById('search-panel')!;
    const isHidden = panel.classList.contains('hidden');

    if (isHidden) {
      panel.classList.remove('hidden');
      document.getElementById('search-input')!.focus();
    } else {
      panel.classList.add('hidden');
      document.querySelector<HTMLInputElement>('#search-input')!.value = '';
      document.getElementById('search-results')!.innerHTML = '';
    }
  }

  async handleSearch(query: string) {
    const resultsContainer = document.getElementById('search-results')!;

    if (!query.trim()) {
      resultsContainer.innerHTML = '';
      return;
    }

    // Simple search implementation
    const results = await this.searchInBible(query);
    this.displaySearchResults(results);
  }

  removeDiacritics(string: string) {
    const rv = string
      .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
      .replace(/[\u0622\u0623\u0625]/g, 'ا')
      .replace(/\u0624/g, 'و')
      .replace(/\u0626/g, 'ي')
      .replace(/\u0649/g, 'ي')
      .replace(/\u0629/g, 'ه')
      .replace(/\u0640/g, '');
    return rv;
  }

  async searchInBible(query: string) {
    const results = [] as SearchResult[];
    const searchTerm = this.removeDiacritics(
      query.toLowerCase().normalize('NFKD'),
    ).normalize('NFKD');

    for (const book of booksMetadata.books) {
      const paddedBookNumber = String(book.bookNumber).padStart(2, '0');
      for (
        let chapterNumber = 1;
        chapterNumber <= book.numberOfChapters;
        chapterNumber++
      ) {
        const paddedChapterNumber = String(chapterNumber).padStart(3, '0');
        const chapterUrl = `/text/${paddedBookNumber}-${book.id}-${paddedChapterNumber}.json`;
        const chapter = await textFiles[chapterUrl]();
        chapter.paragraphs.forEach((paragraph) => {
          paragraph.verses.forEach((verse) => {
            const verseText = this.removeDiacritics(
              verse.text.toLowerCase().normalize('NFKD'),
            ).normalize('NFKD');
            if (verseText.includes(searchTerm)) {
              results.push({
                book: book,
                chapter: chapter,
                verse: verse,
                relevance: this.calculateRelevance(verse.text, searchTerm),
              });
            }
          });
        });
      }
    }

    // Sort by relevance
    results.sort((a, b) => b.relevance - a.relevance);
    return results.slice(0, 20); // Limit to 20 results
  }

  calculateRelevance(text: string, searchTerm: string) {
    const lowerText = text.toLowerCase();
    const lowerTerm = searchTerm.toLowerCase();

    // Simple relevance calculation
    let score = 0;
    const occurrences = (lowerText.match(new RegExp(lowerTerm, 'g')) || [])
      .length;
    score += occurrences * 10;

    // Bonus for exact word matches
    const words = lowerText.split(/\s+/);
    const termWords = lowerTerm.split(/\s+/);
    termWords.forEach((termWord) => {
      if (words.includes(termWord)) {
        score += 5;
      }
    });

    return score;
  }

  displaySearchResults(results: SearchResult[]) {
    const container = document.getElementById('search-results')!;

    if (results.length === 0) {
      container.innerHTML =
        '<div class="search-result">لم يتم العثور على نتائج</div>';
      return;
    }

    container.innerHTML = results
      .map(
        (result) => `
      <div class="search-result" data-book="${result.book.id}" data-chapter="${result.chapter.number}" data-verse="${result.verse.number}">
        <div style="font-weight: bold; margin-bottom: 0.25rem;">
          ${result.book.name} ${result.chapter.number}: ${result.verse.number}
        </div>
        <div style="font-size: 0.9rem;">
          ${this.highlightSearchTerm(this.removeDiacritics(result.verse.text), document.querySelector<HTMLInputElement>('#search-input')!.value)}
        </div>
      </div>
    `,
      )
      .join('');

    // Add click handlers to search results
    container
      .querySelectorAll<HTMLDivElement>('.search-result')
      .forEach((element) => {
        element.addEventListener('click', () => {
          const bookId = element.dataset.book!;
          const chapterNum = parseInt(element.dataset.chapter!, 10);
          const verseNum = parseInt(element.dataset.verse!, 10);

          this.bibleApp.showSearchResult(bookId, chapterNum, verseNum);
          this.toggleSearchPanel(); // Close search panel
        });
      });
  }

  highlightSearchTerm(text: string, searchTerm: string) {
    if (!searchTerm.trim()) return text;
    searchTerm = this.removeDiacritics(searchTerm.normalize('NFKD')).normalize(
      'NFKD',
    );

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi',
    );
    return text
      .normalize('NFKD')
      .replace(regex, '<mark style="background-color: yellow">$1</mark>');
  }

  toggleTheme() {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  }

  openPDF() {
    const book = this.bibleApp.currentBook;
    const chapter = this.bibleApp.currentChapter;
    let path: string;
    if (book?.id === undefined) {
      path = '/pdf/AEB2_website_MAT-REV_ptxp.pdf';
    } else if (chapter?.number === undefined || book?.numberOfChapters === 1) {
      path = `/pdf/AEB2_website_${book.id}_ptxp.pdf`;
    } else if (chapter?.number !== undefined) {
      const chapterString = String(chapter.number).padStart(3, '0');
      path = `/pdf/AEB2_website_${book.id}-${chapterString}_ptxp.pdf`;
    } else {
      throw new Error('Unexpected book or chapter values');
    }

    const url = pdfFiles[path].default;

    if (navigator?.pdfViewerEnabled) {
      const win = window.open(url, '_blank');
      if (win === null) {
        window.location.href = url;
      }
    } else {
      // Due to a bug in Chrome, pdfViewerEnabled is sometimes false even when
      // the built-in PDF viewer is enabled. This line of code should work
      // either way:
      const win = window.open(url);
      if (win === null) {
        window.location.href = url;
      }
    }
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }

  toggleFontPanel() {
    const panel = document.getElementById('font-panel')!;
    panel.classList.toggle('hidden');
  }

  increaseFontSize() {
    const currentSize = parseInt(localStorage.getItem('fontSize') || '16', 10);
    const newSize = Math.min(currentSize + 1, 24); // Max size 24px
    this.updateFontSize(newSize);
  }

  decreaseFontSize() {
    const currentSize = parseInt(localStorage.getItem('fontSize') || '16', 10);
    const newSize = Math.max(currentSize - 1, 12); // Min size 12px
    this.updateFontSize(newSize);
  }

  updateFontSize(size: string | number) {
    const fontSize = typeof size === 'string' ? parseInt(size, 10) : size;
    document.documentElement.style.setProperty(
      '--font-size-base',
      `${fontSize}px`,
    );
    document.getElementById('font-size-display')!.textContent = `${fontSize}px`;
    localStorage.setItem('fontSize', fontSize.toString());

    // Update button states
    this.updateFontButtonStates(fontSize);
  }

  updateFontButtonStates(fontSize: number) {
    const decreaseBtn =
      document.querySelector<HTMLButtonElement>('#font-decrease-btn')!;
    const increaseBtn =
      document.querySelector<HTMLButtonElement>('#font-increase-btn')!;

    // Disable buttons at limits
    decreaseBtn.disabled = fontSize <= 12;
    increaseBtn.disabled = fontSize >= 24;
  }

  initializeFontSize() {
    const savedSize = localStorage.getItem('fontSize') || '16';
    this.updateFontSize(savedSize);
  }

  handleKeyboardShortcuts(e: KeyboardEvent) {
    // Ctrl/Cmd + F for search
    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
      e.preventDefault();
      this.toggleSearchPanel();
    }

    // Escape to close panels
    if (e.key === 'Escape') {
      document.getElementById('search-panel')!.classList.add('hidden');
      document.getElementById('font-panel')!.classList.add('hidden');
    }

    // Arrow keys for navigation in reading view
    if (this.bibleApp.currentView === 'reading') {
      if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey) {
        const nextBtn =
          document.querySelector<HTMLButtonElement>('#next-chapter')!;
        if (!nextBtn.disabled) {
          nextBtn.click();
        }
      } else if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey) {
        const prevBtn =
          document.querySelector<HTMLButtonElement>('#prev-chapter')!;
        if (!prevBtn.disabled) {
          prevBtn.click();
        }
      }
    }
  }

  handleOutsideClick(e: PointerEvent) {
    // Close font panel if clicking outside
    const fontPanel = document.getElementById('font-panel')!;
    const fontBtn = document.getElementById('font-size-btn')!;
    const target = e.target as Node;

    if (
      !fontPanel.classList.contains('hidden') &&
      !fontPanel.contains(target) &&
      !fontBtn.contains(target)
    ) {
      fontPanel.classList.add('hidden');
    }
  }

  showError(message: string) {
    // Simple error display
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #ef4444;
      color: white;
      padding: 1rem 2rem;
      border-radius: 0.5rem;
      z-index: 9999;
      text-align: center;
    `;
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);

    setTimeout(() => {
      document.body.removeChild(errorDiv);
    }, 5000);
  }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.app = new App();
});
